/**
 * WebGPU Device Context Manager
 *
 * Handles adapter/device lifecycle, capability detection, and device-lost
 * recovery. Provides a single shared GPUDevice for the application.
 *
 * Usage:
 *   const ctx = WebGPUContext.getInstance();
 *   if (await ctx.isAvailable()) {
 *       const device = ctx.getDevice();
 *       // ... use device
 *   }
 */
export class WebGPUContext {
    private static instance: WebGPUContext | null = null;

    private adapter: GPUAdapter | null = null;
    private device: GPUDevice | null = null;
    /** True when the device was created with the `bgra8unorm-storage` feature. */
    private _hasBgraStorage = false;

    /**
     * Minimum storage buffers the nebula illumination shader requires.
     * BG0: 2 (densityGrid, polarCache) + BG2: 1 (cellAccum) = 3 total.
     * (permTable removed — shaders use inline PCG32 hash instead.)
     * All accumulation channels are packed into a single CellAccum struct
     * array, and density grids are interleaved — so even GPUs with very
     * low limits (Apple Silicon: 10, theoretical minimum: 4+) work fine.
     */
    static readonly MIN_STORAGE_BUFFERS = 3;

    /** Cached availability result (null = not yet checked) */
    private available: boolean | null = null;

    /** True while an init request is in flight */
    private initialising: Promise<boolean> | null = null;

    private constructor() {}

    static getInstance(): WebGPUContext {
        if (!WebGPUContext.instance) {
            WebGPUContext.instance = new WebGPUContext();
        }
        return WebGPUContext.instance;
    }

    // ── Public API ───────────────────────────────────────────────────

    /**
     * Check whether WebGPU compute is usable on this device.
     * First call triggers adapter/device initialisation; subsequent calls
     * return the cached result.
     */
    async isAvailable(): Promise<boolean> {
        if (this.available !== null) return this.available;
        if (this.initialising) return this.initialising;

        this.initialising = this.init();
        this.available = await this.initialising;
        this.initialising = null;
        return this.available;
    }

    /**
     * Return the live GPUDevice. Throws if not yet initialised or unavailable.
     */
    getDevice(): GPUDevice {
        if (!this.device) {
            throw new Error(
                'WebGPUContext: device not available. Call isAvailable() first.',
            );
        }
        return this.device;
    }

    /**
     * Return the GPUAdapter (useful for querying limits).
     */
    getAdapter(): GPUAdapter {
        if (!this.adapter) {
            throw new Error(
                'WebGPUContext: adapter not available. Call isAvailable() first.',
            );
        }
        return this.adapter;
    }

    /**
     * The canvas format to use for WebGPU-backed canvases.
     *
     * Returns `navigator.gpu.getPreferredCanvasFormat()` when the device
     * was created with the `bgra8unorm-storage` optional feature (supported
     * on all Metal/Apple Silicon and most Vulkan backends). Falls back to
     * `rgba8unorm` — a guaranteed core format — on devices that lack the
     * feature, accepting the driver's format-conversion copy at present time.
     *
     * Call only after `isAvailable()` has resolved `true`.
     */
    get canvasFormat(): GPUTextureFormat {
        if (this._hasBgraStorage) {
            return navigator.gpu.getPreferredCanvasFormat();
        }
        return 'rgba8unorm';
    }

    /**
     * Destroy the device and reset state. Call on shutdown or when the
     * GPU context is no longer needed.
     */
    destroy(): void {
        if (this.device) {
            this.device.destroy();
        }
        this.device = null;
        this.adapter = null;
        this.available = null;
        this._hasBgraStorage = false;
    }

    // ── Internals ────────────────────────────────────────────────────

    private async init(): Promise<boolean> {
        try {
            if (typeof navigator === 'undefined' || !navigator.gpu) {
                console.info('ℹ️  WebGPU not supported in this environment');
                return false;
            }

            this.adapter = await navigator.gpu.requestAdapter({
                powerPreference: 'high-performance',
            });

            if (!this.adapter) {
                console.info('ℹ️  WebGPU adapter unavailable');
                return false;
            }

            // Request the adapter's full storage buffer capacity.
            // Gate on the minimum the shader actually needs (MIN_STORAGE_BUFFERS),
            // then request whatever the adapter supports so future buffers
            // don't require bumping a hardcoded constant.
            const adapterLimit =
                this.adapter.limits.maxStorageBuffersPerShaderStage;

            if (adapterLimit < WebGPUContext.MIN_STORAGE_BUFFERS) {
                console.info(
                    `ℹ️  GPU adapter only supports ${adapterLimit} storage buffers ` +
                    `(need ${WebGPUContext.MIN_STORAGE_BUFFERS}) — WebGPU unavailable`,
                );
                return false;
            }

            this.device = await this.adapter.requestDevice({
                requiredFeatures: this.adapter.features.has('bgra8unorm-storage')
                    ? (['bgra8unorm-storage'] as GPUFeatureName[])
                    : [],
                requiredLimits: {
                    maxStorageBuffersPerShaderStage: adapterLimit,
                    maxStorageBufferBindingSize:
                        this.adapter.limits.maxStorageBufferBindingSize,
                    maxBufferSize:
                        this.adapter.limits.maxBufferSize,
                    // Compositor framebuffer scales with viewport size, so
                    // request the adapter's full 2D texture cap. Without
                    // this the device defaults to 8192² and the nebula
                    // compositor crashes on high-DPI / zoomed-in viewports
                    // (~11k px square on a 4K Retina display).
                    maxTextureDimension2D:
                        this.adapter.limits.maxTextureDimension2D,
                },
            });

            // Handle device loss: mark unavailable and log
            this._hasBgraStorage = this.device.features.has('bgra8unorm-storage');
            this.device.lost.then((info) => {
                console.warn(
                    `⚠️  WebGPU device lost (${info.reason}): ${info.message}`,
                );
                this.device = null;
                this.available = null;
            });

            console.info(
                `🚀 WebGPU compute available (${this.adapter.info?.device ?? 'unknown device'})`,
            );
            return true;
        } catch (err) {
            console.warn('⚠️  WebGPU initialisation failed:', err);
            return false;
        }
    }
}
